import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = { class: "py-4" };
const _hoisted_2 = { class: "relative" };
const _hoisted_3 = { class: "space-y-6" };
const _hoisted_4 = {
    key: 0,
    class: "w-4 h-4 text-white",
    fill: "currentColor",
    viewBox: "0 0 20 20"
};
const _hoisted_5 = { class: "ml-4 flex-1" };
const _hoisted_6 = { class: "flex items-center justify-between" };
const _hoisted_7 = {
    key: 0,
    class: "text-right"
};
const _hoisted_8 = { class: "text-xs text-gray-500" };
const _hoisted_9 = {
    key: 0,
    class: "mt-6 text-center text-sm text-gray-500"
};
import { computed } from 'vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'OrderProgressTimeline',
    props: {
        currentStatus: {},
        orderType: {},
        statusHistory: {},
        estimatedTime: {}
    },
    setup(__props) {
        const props = __props;
        // Timeline steps based on order type
        const timelineSteps = computed(() => {
            const baseSteps = [
                {
                    status: 'pending',
                    title: 'Bestellung eingegangen',
                    description: 'Wir haben deine Bestellung erhalten und prüfen sie',
                    icon: 'receipt'
                },
                {
                    status: 'confirmed',
                    title: 'Bestellung bestätigt',
                    description: 'Deine Bestellung wurde bestätigt',
                    icon: 'check'
                },
                {
                    status: 'preparing',
                    title: 'Wird zubereitet',
                    description: 'Unser Team bereitet deine Bestellung frisch zu',
                    icon: 'cooking'
                }
            ];
            // Add different final steps based on order type
            if (props.orderType === 'delivery') {
                baseSteps.push({
                    status: 'out_for_delivery',
                    title: 'Auf dem Weg',
                    description: 'Deine Bestellung ist unterwegs zu dir',
                    icon: 'truck'
                }, {
                    status: 'delivered',
                    title: 'Zugestellt',
                    description: 'Deine Bestellung wurde erfolgreich zugestellt',
                    icon: 'delivered'
                });
            }
            else {
                baseSteps.push({
                    status: 'ready',
                    title: 'Bereit zur Abholung',
                    description: 'Deine Bestellung ist fertig und kann abgeholt werden',
                    icon: 'ready'
                }, {
                    status: 'delivered',
                    title: 'Abgeholt',
                    description: 'Deine Bestellung wurde erfolgreich abgeholt',
                    icon: 'delivered'
                });
            }
            // Add status information from history
            return baseSteps.map(step => {
                const historyEntry = props.statusHistory.find(h => h.status === step.status ||
                    (props.orderType === 'delivery' && step.status === 'out_for_delivery' && h.status === 'ready'));
                const stepIndex = getStatusIndex(step.status);
                const currentIndex = getStatusIndex(getDisplayStatus(props.currentStatus));
                return {
                    ...step,
                    completed: stepIndex < currentIndex || (step.status === 'delivered' && props.currentStatus === 'delivered'),
                    current: stepIndex === currentIndex && step.status !== 'delivered',
                    timestamp: historyEntry?.timestamp
                };
            });
        });
        // Calculate progress height for the progress line
        const progressHeight = computed(() => {
            const currentIndex = getStatusIndex(props.currentStatus);
            const totalSteps = timelineSteps.value.length;
            if (totalSteps <= 1)
                return '0rem';
            // Calculate progress as percentage of the distance between circles
            const progress = Math.min(currentIndex / (totalSteps - 1), 1);
            // Fixed total height of 16rem
            const totalDistance = 16;
            return `${progress * totalDistance}rem`;
        });
        // Calculate estimated completion time
        const estimatedCompletionTime = computed(() => {
            if (!props.estimatedTime)
                return null;
            const now = new Date();
            const estimated = new Date(now.getTime() + props.estimatedTime * 60000);
            return estimated.toLocaleString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
            });
        });
        // Helper functions
        function getStatusIndex(status) {
            const statusOrder = props.orderType === 'delivery'
                ? ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered']
                : ['pending', 'confirmed', 'preparing', 'ready', 'delivered'];
            return statusOrder.indexOf(status);
        }
        function getDisplayStatus(status) {
            if (props.orderType === 'delivery' && status === 'ready') {
                return 'out_for_delivery';
            }
            return status;
        }
        function getStepCircleClass(step) {
            if (step.completed) {
                return 'bg-green-500 border-green-500 text-white';
            }
            else if (step.current) {
                return 'bg-blue-500 border-blue-500 text-white';
            }
            else {
                return 'bg-white border-gray-300 text-gray-300';
            }
        }
        function formatTimestamp(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
            if (diffInMinutes < 1) {
                return 'gerade eben';
            }
            else if (diffInMinutes < 60) {
                return `vor ${diffInMinutes} Min.`;
            }
            else {
                return date.toLocaleString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createCommentVNode(" Timeline Line - only between first and last circle "),
                    _cache[3] || (_cache[3] = _createElementVNode("div", {
                        class: "absolute left-4 w-0.5 bg-gray-200",
                        style: { top: '1rem', height: '16rem' }
                    }, null, -1 /* CACHED */)),
                    _createCommentVNode(" Progress Line - only between first and last circle "),
                    _createElementVNode("div", {
                        class: "absolute left-4 w-0.5 bg-green-500 transition-all duration-500",
                        style: _normalizeStyle({ top: '1rem', height: progressHeight.value })
                    }, null, 4 /* STYLE */),
                    _createCommentVNode(" Status Steps "),
                    _createElementVNode("div", _hoisted_3, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelineSteps.value, (step) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: step.status,
                                class: "relative flex items-center"
                            }, [
                                _createCommentVNode(" Step Circle "),
                                _createElementVNode("div", {
                                    class: _normalizeClass([getStepCircleClass(step), "relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300"])
                                }, [
                                    _createCommentVNode(" Completed Icon "),
                                    (step.completed)
                                        ? (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[0] || (_cache[0] = [
                                                _createElementVNode("path", {
                                                    "fill-rule": "evenodd",
                                                    d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                                                    "clip-rule": "evenodd"
                                                }, null, -1 /* CACHED */)
                                            ]))]))
                                        : (step.current)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                _createCommentVNode(" Current/Pending Icon "),
                                                _cache[1] || (_cache[1] = _createElementVNode("div", { class: "w-3 h-3 bg-current rounded-full animate-pulse" }, null, -1 /* CACHED */))
                                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                _createCommentVNode(" Future Icon "),
                                                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "w-3 h-3 bg-current rounded-full opacity-50" }, null, -1 /* CACHED */))
                                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                ], 2 /* CLASS */),
                                _createCommentVNode(" Step Content "),
                                _createElementVNode("div", _hoisted_5, [
                                    _createElementVNode("div", _hoisted_6, [
                                        _createElementVNode("div", null, [
                                            _createElementVNode("h4", {
                                                class: _normalizeClass([step.completed || step.current ? 'text-gray-900 font-medium' : 'text-gray-500', "text-sm"])
                                            }, _toDisplayString(step.title), 3 /* TEXT, CLASS */),
                                            _createElementVNode("p", {
                                                class: _normalizeClass([step.completed || step.current ? 'text-gray-600' : 'text-gray-400', "text-xs mt-1"])
                                            }, _toDisplayString(step.description), 3 /* TEXT, CLASS */)
                                        ]),
                                        _createCommentVNode(" Timestamp "),
                                        (step.timestamp)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                                _createElementVNode("p", _hoisted_8, _toDisplayString(formatTimestamp(step.timestamp)), 1 /* TEXT */)
                                            ]))
                                            : _createCommentVNode("v-if", true)
                                    ])
                                ])
                            ]));
                        }), 128 /* KEYED_FRAGMENT */))
                    ])
                ]),
                (estimatedCompletionTime.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Geschätzte Fertigstellung: " + _toDisplayString(estimatedCompletionTime.value), 1 /* TEXT */))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
